import React, { useState, useRef } from 'react';
import { Accordion } from '@uhg-abyss/web/ui/Accordion';
import { Button } from '@uhg-abyss/web/ui/Button';
import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import { Tooltip } from '@uhg-abyss/web/ui/Tooltip';
import ConfirmDialog from '../ConfirmDialog/ConfirmDialog';
import { saveEditedInfo } from '../../service/uploadService';

const renderValue = (value) => {
  if (value === null || value === undefined) return null;

  if (Array.isArray(value)) {
    return (
      <ul style={{ margin: '4px 0', paddingLeft: '20px' }}>
        {value.map((item, i) => (
          <li key={i}>{typeof item === 'object' ? renderValue(item) : item}</li>
        ))}
      </ul>
    );
  }

  if (typeof value === 'object') {
    return (
      <div style={{ marginLeft: '16px' }}>
        {Object.entries(value).map(([k, v]) => (
          <div key={k} style={{ marginBottom: '1px' }}>
            <strong>{k}:</strong> {typeof v === 'string' ? v : renderValue(v)}
          </div>
        ))}
      </div>
    );
  }

  return <span>{String(value)}</span>;
};

const parseContentToJson = (ref, originalData) => {
  if (!ref.current) return originalData;
  const result = {};
  const topDivs = ref.current.querySelectorAll(':scope > div');
  topDivs.forEach((div) => {
    const strong = div.querySelector('strong');
    if (strong) {
      const key = strong.textContent.replace(/:$/, '').trim();
      if (originalData[key] !== undefined) {
        if (typeof originalData[key] === 'string') {
          const textContent = div.innerText.replace(strong.textContent, '').trim();
          result[key] = textContent;
        } else if (Array.isArray(originalData[key])) {
          const items = div.querySelectorAll('li');
          if (items.length > 0) {
            result[key] = Array.from(items).map((li) => li.innerText.trim());
          } else {
            const textContent = div.innerText.replace(strong.textContent, '').trim();
            result[key] = textContent.split('\n').map((s) => s.trim()).filter(Boolean);
          }
        } else if (typeof originalData[key] === 'object') {
          result[key] = parseNestedObject(div, originalData[key]);
        }
      } else {
        const textContent = div.innerText.replace(strong.textContent, '').trim();
        result[key] = textContent;
      }
    }
  });
  return Object.keys(result).length > 0 ? result : originalData;
};

const parseNestedObject = (container, originalObj) => {
  const result = {};
  const nestedDivs = container.querySelectorAll(':scope > div > div');
  if (nestedDivs.length > 0) {
    nestedDivs.forEach((div) => {
      const strong = div.querySelector('strong');
      if (strong) {
        const key = strong.textContent.replace(/:$/, '').trim();
        if (originalObj[key] !== undefined) {
          if (typeof originalObj[key] === 'string') {
            const textContent = div.innerText.replace(strong.textContent, '').trim();
            result[key] = textContent;
          } else if (Array.isArray(originalObj[key])) {
            const items = div.querySelectorAll('li');
            if (items.length > 0) {
              result[key] = Array.from(items).map((li) => li.innerText.trim());
            } else {
              const textContent = div.innerText.replace(strong.textContent, '').trim();
              result[key] = textContent.split('\n').map((s) => s.trim()).filter(Boolean);
            }
          } else if (typeof originalObj[key] === 'object') {
            result[key] = parseNestedObject(div, originalObj[key]);
          }
        } else {
          const textContent = div.innerText.replace(strong.textContent, '').trim();
          result[key] = textContent;
        }
      }
    });
  } else {
    // Flat nested: just grab text for each key from originalObj
    Object.keys(originalObj).forEach((key) => {
      result[key] = originalObj[key];
    });
  }
  return Object.keys(result).length > 0 ? result : originalObj;
};

const base64Decode = (value) => {
  if (value === null || value === undefined) return null;
  if (typeof value === 'string') {
    try {
      const decoded = decodeURIComponent(escape(atob(value)));
      return JSON.parse(decoded);
    } catch (e) {
      return value;
    }
  }
  return value;
};

const isNonEmptyObject = (value) =>
  !!value && typeof value === 'object' && Object.keys(value).length > 0;

const EMPTY_VALUE_PLACEHOLDER = 'None (not mentioned in document)';

// The model emits [] for fields it could not find; show the same wording it uses for known-missing fields.
const withEmptyArrayPlaceholders = (value) => {
  if (Array.isArray(value)) {
    return value.length === 0
      ? [EMPTY_VALUE_PLACEHOLDER]
      : value.map(withEmptyArrayPlaceholders);
  }
  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value).map(([key, item]) => [key, withEmptyArrayPlaceholders(item)])
    );
  }
  return value;
};

const getSections = (file) => ({
  triageFields: withEmptyArrayPlaceholders(
    base64Decode(file?.triage_information ?? file?.triageInformation) ?? {}
  ),
  synopsisData: withEmptyArrayPlaceholders(
    base64Decode(file?.descriptive_summary ?? file?.descriptiveSummary) ?? {}
  ),
});

const isProcessFailed = (file) => Number(file?.status?.code) === 521;

// Lets the layout know whether the results panel will render content or only an error card.
export const hasProcessedContent = (file) => {
  if (isProcessFailed(file)) return false;
  const { triageFields, synopsisData } = getSections(file);
  return isNonEmptyObject(synopsisData) || isNonEmptyObject(triageFields);
};

function FileData({ file, onEditChange, requestId }) {
  const [triageCopied, setTriageCopied] = useState(false);
  const [synopsisCopied, setSynopsisCopied] = useState(false);
  const [isEditingSynopsis, setIsEditingSynopsis] = useState(false);
  const [isEditingTriage, setIsEditingTriage] = useState(false);
  const [synopsisBackup, setSynopsisBackup] = useState('');
  const [triageBackup, setTriageBackup] = useState('');
  const [synopsisChanged, setSynopsisChanged] = useState(false);
  const [triageChanged, setTriageChanged] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, title: '', message: '', onConfirm: null });
  const { triageFields, synopsisData } = getSections(file);
  const ocrErroredFileNames = Array.isArray(file?.ocrErrors)
    ? file.ocrErrors
        .map((errorItem) => errorItem?.fileName)
        .filter(Boolean)
    : [];
  const invalidFileNames = Array.isArray(file?.invalidFiles)
    ? file.invalidFiles.filter(Boolean)
    : [];
  const processingErrorNote =
    ocrErroredFileNames.length > 0
      ? `Processing error: ${ocrErroredFileNames.join(', ')}`
      : null;
  const invalidFilesNote =
    invalidFileNames.length > 0
      ? `Validation error: ${invalidFileNames.join(', ')}`
      : null;
  const noteMessage = [invalidFilesNote, processingErrorNote].filter(Boolean).join(' ');
  const nonEnglishFileNames = (Array.isArray(file?.nonEnglishLanguageDetected)
    ? file.nonEnglishLanguageDetected
    : []
  )
    .map((item) => (typeof item === 'string' ? item : item?.fileName))
    .filter(Boolean);
  const processFailed = isProcessFailed(file);
  const hasSynopsisData = !processFailed && isNonEmptyObject(synopsisData);
  const hasTriageData = !processFailed && isNonEmptyObject(triageFields);
  const triageRef = useRef(null);
  const synopsisRef = useRef(null);
  const synopsisKeysRef = useRef([]);
  const triageKeysRef = useRef([]);
  const lastValidSynopsisRef = useRef('');
  const lastValidTriageRef = useRef('');

  const handleCopy = (ref, setCopied) => {
    const html = ref.current?.innerHTML || '';
    const text = ref.current?.innerText || '';
    const blob = new Blob([html], { type: 'text/html' });
    const textBlob = new Blob([text], { type: 'text/plain' });
    navigator.clipboard.write([
      new ClipboardItem({ 'text/html': blob, 'text/plain': textBlob }),
    ]).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };

  const getKeysFromRef = (ref) => {
    if (!ref.current) return [];
    const strongElements = ref.current.querySelectorAll('strong');
    return Array.from(strongElements).map((el) => el.textContent.trim());
  };

  const handleEdit = (ref, setBackup, setEditing, setChanged, keysRef, lastValidRef) => {
    // Make all <strong> elements non-editable to prevent cursor from entering them
    const strongElements = ref.current?.querySelectorAll('strong') || [];
    strongElements.forEach((strong) => {
      strong.setAttribute('contenteditable', 'false');
      // Ensure there's always an editable text node after the strong
      const next = strong.nextSibling;
      if (!next || (next.nodeType === Node.TEXT_NODE && next.textContent === '')) {
        const space = document.createTextNode(' ');
        strong.after(space);
      }
    });
    const html = ref.current?.innerHTML || '';
    setBackup(html);
    setChanged(false);
    keysRef.current = getKeysFromRef(ref);
    lastValidRef.current = html;
    setEditing(true);
    onEditChange?.(true);
  };

  const handleKeyDown = (e, ref) => {
    if (e.key === 'Backspace') {
      const selection = window.getSelection();
      if (!selection.rangeCount) return;
      const range = selection.getRangeAt(0);
      const container = range.startContainer;

      // If cursor is at position 0 of a text node that is right after a <strong>
      if (range.collapsed && range.startOffset === 0) {
        let node = container;
        // Walk up to find the immediate child div of content-body
        while (node && node !== ref.current) {
          const prev = node.previousSibling;
          if (prev && prev.nodeType === Node.ELEMENT_NODE && prev.tagName === 'STRONG') {
            // We're right after a non-editable strong - prevent backspace from jumping
            e.preventDefault();
            return;
          }
          // If we're at offset 0 of the first child of a div, check if backspace would merge with previous div
          if (!prev && node.parentNode && node.parentNode !== ref.current) {
            node = node.parentNode;
          } else {
            break;
          }
        }
      }

      // If cursor is inside an li and it's the last li, prevent removing it entirely
      let liNode = container.nodeType === Node.TEXT_NODE ? container.parentNode : container;
      while (liNode && liNode !== ref.current && liNode.tagName !== 'LI') {
        liNode = liNode.parentNode;
      }
      if (liNode && liNode.tagName === 'LI') {
        const list = liNode.parentNode;
        if (list && (list.tagName === 'UL' || list.tagName === 'OL')) {
          const items = list.querySelectorAll('li');
          // If last li and cursor at start with no text, prevent removing the structure
          if (items.length === 1 && range.collapsed && range.startOffset === 0 && !liNode.innerText?.trim()) {
            e.preventDefault();
            return;
          }
        }
      }
    }
  };

  const handleInput = (ref, backup, setChanged, keysRef, lastValidRef) => {
    const currentText = ref.current?.innerText?.trim() || '';

    // Prevent clearing all content
    if (!currentText) {
      ref.current.innerHTML = lastValidRef.current;
      return;
    }

    // Prevent removing keys
    const currentKeys = getKeysFromRef(ref);
    const originalKeys = keysRef.current;
    const allKeysPresent = originalKeys.every((key) =>
      currentKeys.some((ck) => ck === key)
    );

    if (!allKeysPresent) {
      ref.current.innerHTML = lastValidRef.current;
      return;
    }

    // Ensure each <strong> is non-editable and always has an editable sibling after it
    const allStrongs = ref.current.querySelectorAll('strong');
    let cursorFixed = false;
    allStrongs.forEach((strong) => {
      strong.setAttribute('contenteditable', 'false');
      const parent = strong.parentNode;

      // Remove empty list containers (ul/ol with no li or empty li)
      let nextSibling = strong.nextSibling;
      while (nextSibling) {
        const next = nextSibling.nextSibling;
        if (nextSibling.nodeType === Node.ELEMENT_NODE) {
          const tag = nextSibling.tagName?.toLowerCase();
          if ((tag === 'ul' || tag === 'ol') && !nextSibling.innerText?.trim()) {
            nextSibling.remove();
            nextSibling = next;
            continue;
          }
        }
        nextSibling = next;
      }

      // Check if there's meaningful editable content after the strong
      nextSibling = strong.nextSibling;
      let hasEditableContent = false;
      while (nextSibling) {
        if (nextSibling.nodeType === Node.ELEMENT_NODE) {
          // Only count elements that have actual text content
          if (nextSibling.innerText?.trim()) {
            hasEditableContent = true;
            break;
          }
        }
        if (nextSibling.nodeType === Node.TEXT_NODE && nextSibling.textContent.replace(/[\u200B\u00A0]/g, '').trim().length > 0) {
          hasEditableContent = true;
          break;
        }
        nextSibling = nextSibling.nextSibling;
      }

      if (!hasEditableContent) {
        // Remove any leftover empty nodes between strong and end of parent
        let toClean = strong.nextSibling;
        while (toClean) {
          const nextToClean = toClean.nextSibling;
          if (toClean.nodeType === Node.TEXT_NODE && !toClean.textContent.replace(/[\u200B\u00A0]/g, '').trim()) {
            toClean.remove();
          } else if (toClean.nodeType === Node.ELEMENT_NODE && !toClean.innerText?.trim()) {
            toClean.remove();
          }
          toClean = nextToClean;
        }

        // Insert a non-breaking space as editable area after strong
        const spacer = document.createTextNode(' \u00A0');
        parent.appendChild(spacer);

        // Move cursor into this spacer (only for the first fix to avoid jumping)
        if (!cursorFixed) {
          cursorFixed = true;
          try {
            const selection = window.getSelection();
            const range = document.createRange();
            range.setStart(spacer, 1);
            range.collapse(true);
            selection.removeAllRanges();
            selection.addRange(range);
          } catch (e) { /* ignore range errors */ }
        }
      }
    });

    lastValidRef.current = ref.current.innerHTML;
    setChanged(ref.current.innerHTML !== backup);
  };

  const handleCancel = (ref, backup, setEditing, setChanged) => {
    setConfirmDialog({
      open: true,
      title: 'Discard Changes',
      message: 'Are you sure you want to cancel? Any unsaved changes will be lost.',
      onConfirm: () => {
        if (ref.current) {
          ref.current.innerHTML = backup;
        }
        setChanged(false);
        setEditing(false);
        onEditChange?.(false);
        setConfirmDialog({ open: false, title: '', message: '', onConfirm: null });
      },
    });
  };

  const handleSave = (setEditing, setChanged, section) => {
    setConfirmDialog({
      open: true,
      title: 'Save Changes',
      message: 'Are you sure you want to save the changes?',
      confirmLabel: 'Confirm',
      cancelLabel: 'Keep Editing',
      onConfirm: () => {
        if (requestId) {
          const updatedData = section === 'synopsis'
            ? parseContentToJson(synopsisRef, synopsisData)
            : parseContentToJson(triageRef, triageFields);
          saveEditedInfo(requestId, section === 'synopsis' ? 'summary' : 'triage', updatedData).catch((err) => {
            console.error('Failed to save:', err);
          });
        }
        setChanged(false);
        setEditing(false);
        onEditChange?.(false);
        setConfirmDialog({ open: false, title: '', message: '', onConfirm: null });
      },
    });
  };

  const closeDialog = () => {
    setConfirmDialog({ open: false, title: '', message: '', onConfirm: null });
  };

  return (
    <div className="file-data-container">
      {(hasSynopsisData || hasTriageData) ? (
        <div className="file-data-disclaimer">
          Disclaimer: Please verify AI‑generated information against the original source medical records.
        </div>
      ) : (
        <div className="file-data-error-card" role="alert">
          <span className="file-data-error-icon">
            <IconSymbol icon="error" size="40px" />
          </span>
          <h3 className="file-data-error-title">
          {'Something went wrong while processing, please try again.'}
          </h3>
          {/* <p className="file-data-error-text">
            {processFailed
              ? 'We could not generate the Synopsis and Triage Information for these files. Please review the files and try again.'
              : 'Please try again in a few moments.'}
          </p> */}
        </div>
      )}
      {(hasSynopsisData || hasTriageData) && noteMessage && (
        <div className="file-data-note">
          <div>Following files information is not present in the Triage Information and Synopsis:</div>
          <ul className="file-data-note-list">
            {invalidFilesNote && <li>{invalidFilesNote}</li>}
            {processingErrorNote && <li>{processingErrorNote}</li>}
          </ul>
        </div>
      )}
      {(hasSynopsisData || hasTriageData) && nonEnglishFileNames.length > 0 && (
        <div className="file-data-note">
          <div>Below documents contain Non-English language text, results may be inaccurate:</div>
          <ul className="file-data-note-list">
            {nonEnglishFileNames.map((fileName) => (
              <li key={fileName}>{fileName}</li>
            ))}
          </ul>
        </div>
      )}
      {(hasSynopsisData || hasTriageData) && (
        <div className="sections-row">
          {hasSynopsisData && (
            <div className="section-column">
            <Accordion defaultValue="sandbox-1" isCollapsible>
            <Accordion.Item value="sandbox-1">
              <Accordion.Header>Synopsis</Accordion.Header>
              <Accordion.Content>
                <div className="content-bordered-box">
                  <div className="content-copy-header">
                    {isEditingSynopsis ? (
                      <>
                        <Button
                          variant="outline"
                          size="$sm"
                          onClick={() => handleCancel(synopsisRef, synopsisBackup, setIsEditingSynopsis, setSynopsisChanged)}
                          style={{ marginRight: '8px' }}
                        >
                          Cancel
                        </Button>
                        <Button
                          disabled={!synopsisChanged}
                          onClick={() => handleSave(setIsEditingSynopsis, setSynopsisChanged, 'synopsis')}
                        >
                          Save
                        </Button>
                      </>
                    ) : (
                      <>
                        <Tooltip content="Copy" align="center">
                          <Button className="button-style"
                            variant="outline"
                            size="$sm"
                            before={synopsisCopied ? '✓' : '📋'}
                            onClick={() => handleCopy(synopsisRef, setSynopsisCopied)}
                          >
                            <IconSymbol icon={synopsisCopied ? 'check' : 'content_copy'} />
                          </Button>
                        </Tooltip>
                        <Tooltip content="Edit" align="center">
                          <Button className="button-style"
                            variant="ghost" rounded size="$sm"
                            before="✏️"
                            onClick={() => handleEdit(synopsisRef, setSynopsisBackup, setIsEditingSynopsis, setSynopsisChanged, synopsisKeysRef, lastValidSynopsisRef)}
                            style={{ marginRight: '8px' }}
                          >
                            <IconSymbol icon="edit"/>
                          </Button>
                        </Tooltip>
                      </>
                    )}
                  </div>
                  <div
                    className="content-body"
                    ref={synopsisRef}
                    contentEditable={isEditingSynopsis}
                    suppressContentEditableWarning
                    onKeyDown={(e) => isEditingSynopsis && handleKeyDown(e, synopsisRef)}
                    onInput={() => handleInput(synopsisRef, synopsisBackup, setSynopsisChanged, synopsisKeysRef, lastValidSynopsisRef)}
                    style={isEditingSynopsis ? { border: '1px solid #0070d2', borderRadius: '4px', padding: '8px', outline: 'none' } : {}}
                  >
                    {Object.entries(synopsisData).map(([section, content]) => (
                      <div key={section} style={{ marginBottom: '0px' }}>
                        <strong contentEditable={false} style={{ fontSize: '1.05em' }}>{section}</strong>
                        {renderValue(content)}
                      </div>
                    ))}
                  </div>
                </div>
              </Accordion.Content>
            </Accordion.Item>
            </Accordion>
            </div>
          )}
          {hasTriageData && (
            <div className="section-column">
            <Accordion defaultValue="sandbox-2" isCollapsible>
            <Accordion.Item value="sandbox-2">
              <Accordion.Header>Triage Information</Accordion.Header>
              <Accordion.Content>
                <div className="content-bordered-box">
                  <div className="content-copy-header">
                    {isEditingTriage ? (
                      <>
                        <Button
                          variant="outline"
                          size="$sm"
                          onClick={() => handleCancel(triageRef, triageBackup, setIsEditingTriage, setTriageChanged)}
                          style={{ marginRight: '8px' }}
                        >
                          Cancel
                        </Button>
                        <Button
                          disabled={!triageChanged}
                          onClick={() => handleSave(setIsEditingTriage, setTriageChanged, 'triage')}
                        >
                          Save
                        </Button>
                      </>
                    ) : (
                      <>
                        <Tooltip content="Copy" align="center">
                          <Button className="button-style"
                            variant="outline"
                            size="$sm"
                            before={triageCopied ? '✓' : '📋'}
                            onClick={() => handleCopy(triageRef, setTriageCopied)}
                          >
                            <IconSymbol icon={triageCopied ? 'check' : 'content_copy'} />
                          </Button>
                        </Tooltip>
                        <Tooltip content="Edit" align="center">
                          <Button className="button-style"
                            variant="ghost" rounded size="$sm"
                            before="✏️"
                            onClick={() => handleEdit(triageRef, setTriageBackup, setIsEditingTriage, setTriageChanged, triageKeysRef, lastValidTriageRef)}
                          >
                            <IconSymbol icon="edit"/>
                          </Button>
                        </Tooltip>
                      </>
                    )}
                  </div>
                  <div
                    className="content-body"
                    ref={triageRef}
                    contentEditable={isEditingTriage}
                    suppressContentEditableWarning
                    onKeyDown={(e) => isEditingTriage && handleKeyDown(e, triageRef)}
                    onInput={() => handleInput(triageRef, triageBackup, setTriageChanged, triageKeysRef, lastValidTriageRef)}
                    style={isEditingTriage ? { border: '1px solid #0070d2', borderRadius: '4px', padding: '8px', outline: 'none' } : {}}
                  >
                    {Object.entries(triageFields).map(([key, value]) => (
                      <div key={key} style={{ marginBottom: '1px' }}>
                        <strong contentEditable={false}>{key}:</strong>
                        {key === 'Odd Procedures' && Array.isArray(value) ? (
                          <ol style={{ margin: '4px 0', paddingLeft: '24px' }}>
                            {value.map((item, i) => (
                              <li key={i} style={{ marginBottom: '8px' }}>
                                {typeof item === 'object'
                                  ? Object.entries(item).map(([k, v]) => (
                                      <div key={k}>
                                        <strong>{k}:</strong> {String(v)}
                                      </div>
                                    ))
                                  : item}
                              </li>
                            ))}
                          </ol>
                        ) : typeof value === 'string' ? (
                          ` ${value}`
                        ) : (
                          renderValue(value)
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </Accordion.Content>
            </Accordion.Item>
            </Accordion>
            </div>
          )}
        </div>
      )}
      <ConfirmDialog
        open={confirmDialog.open}
        title={confirmDialog.title}
        message={confirmDialog.message}
        onConfirm={confirmDialog.onConfirm}
        onCancel={closeDialog}
        confirmLabel={confirmDialog.confirmLabel}
        cancelLabel={confirmDialog.cancelLabel}
      />
    </div>
  );
}

export default FileData;